import React from "react";
import { cilArrowBottom, cilCopy } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
// CCol, CRow removed - unused (per Copilot review)
import { CCard, CCardBody } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import type * as T from "@src/types";
import { formatDateTimeUTC } from "@src/utils/dateUtils";
import {
  type PositionRow,
  buildPositionDisplayRows,
  buildRunExport,
  calculateDuration,
  formatRunExportAsJSON,
  safeCalculatePercentage,
} from "@src/utils/formatters";
import {
  calculatePositionProfit,
  getPositionGrossProfit,
  getPositionNetProfit,
} from "@src/utils/positionProfit";
import {
  type ExecutionInterval,
  OUTSIDE_EXECUTION_INTERVAL_LABEL,
  TRACKING_ENDED_EXPLANATION,
  TRACKING_ENDED_LABEL,
  getAccountingCoverageMetrics,
  getTrackingEndedMovementTime,
  hasOnlyTrackingEndedPositions,
  isFactOutsideExecutionInterval,
  isPositionTrackingEnded,
  resolvePositionExecutionInterval,
  sortRunPositions,
} from "@src/utils/positionStatus";
import { HighTable, arrayDataFrame } from "hightable";

import { ManualOrderRow } from "./ManualOrderRow";
import { OpenPositionCell } from "./OpenPositionCell";
import { RunMovementCell } from "./RunMovementCell";
import { PositionOnlyCell } from "./PositionOnlyCell";
import { AccountingCoverageNotice } from "./AccountingCoverageNotice";
import { TrackingEndedFacts } from "./TrackingEndedFacts";

interface Props {
  run: T.ApiRun;
  runResult: T.RunResult;
  type: "backtest" | "execution";
  virtualizedTable?: boolean;
}

// =============================================================================
// PARTIAL CLOSE PROFIT HELPERS
// TODO: Temporal solution - the whole framework should support multi-order
// position-movements instead of the current 1-position = 1-open + 1-close model
// =============================================================================

interface PartialCloseProfitParams {
  exitPrice: number;
  exitAmount: number;
  exitFees: number;
  entryPrice: number;
  entryAmount: number;
  collateralIn: number | null;
  directionName?: string;
  typeName?: string;
}

/** Calculate profit for a partial close movement (not the final close) */
const calculatePartialCloseProfit = (params: PartialCloseProfitParams): number => {
  const { exitPrice, exitAmount, exitFees, entryPrice, directionName, typeName } = params;
  // Long: (exit - entry) * amount, Short: (entry - exit) * amount
  const isLong = directionName === "long" || typeName === "spot";
  const grossProfit = isLong
    ? (exitPrice - entryPrice) * exitAmount
    : (entryPrice - exitPrice) * exitAmount;
  return grossProfit - exitFees;
};

/** Calculate profit % for a partial close using proportional collateral */
const calculatePartialCloseProfitPercent = (
  profit: number,
  params: PartialCloseProfitParams,
): number => {
  const { exitAmount, entryAmount, collateralIn } = params;
  // Guard against missing/invalid values - checks cover undefined, null, and 0
  if (profit === 0 || !collateralIn || !entryAmount || !exitAmount) return 0;
  const collateralRatio = exitAmount / entryAmount;
  const proportionalCollateral = collateralIn * collateralRatio;
  return (profit / proportionalCollateral) * 100;
};

interface TableRow {
  id: number;
  position: string;
  type: string;
  dateTime: string;
  duration: string;
  price: string;
  contracts: string;
  positionSize: string;
  leverage: string;
  collateral: string;
  fees: string;
  profit: string;
  profitPercent: string;
  cumProfit: string;
  cumProfitPercent: string;
  [key: string]: string | number; // Index signature to allow dynamic key access
}

export const getPositionExecutionInterval = (
  position: T.AppPosition,
  run: T.ApiRun,
  runResult: T.RunResult,
): ExecutionInterval =>
  resolvePositionExecutionInterval(position, {
    start_ts: run.start_ts,
    end_ts: run.end_ts,
    executionBoundaries: runResult.executionBoundaries,
    group: "_is_group_synthetic" in run && !!run._is_group_synthetic,
  });

export const buildVirtualizedPositionRows = (
  positions: T.AppPosition[],
  tableRows: TableRow[],
  run: T.ApiRun,
  runResult: T.RunResult,
): { headers: string[]; rows: (string | number)[][] } => {
  const headers = [
    "Movement ID",
    "Position",
    "Type",
    "Date/Time",
    "Duration",
    "Price",
    "Contracts",
    "Position Size",
    "Leverage",
    "Collateral",
    "Fees",
    "Profit",
    "Profit %",
    "Cum. Profit",
    "Cum. Profit %",
    "Status",
  ];
  const rows = buildPositionDisplayRows(positions, tableRows).flatMap((row) => {
    const trackingEnded = isPositionTrackingEnded(row.position);
    const interval = trackingEnded
      ? getPositionExecutionInterval(row.position, run, runResult)
      : null;
    const status = (time: string) =>
      `${TRACKING_ENDED_LABEL}: ${TRACKING_ENDED_EXPLANATION}${isFactOutsideExecutionInterval(time, interval) ? `; ${OUTSIDE_EXECUTION_INTERVAL_LABEL}` : ""}`;
    if (row.positionOnly)
      return [
        [
          "",
          row.positionId,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          status(row.position.start_ts),
        ],
      ];
    return (row.movements ?? []).map((movement) => {
      const fact = trackingEnded
        ? row.position.movements.find((entry) => entry.id === movement.id)
        : undefined;
      return [
        movement.id,
        movement.position,
        movement.type,
        movement.dateTime,
        movement.duration,
        movement.price,
        movement.contracts,
        movement.positionSize,
        movement.leverage,
        movement.collateral,
        movement.fees,
        movement.profit,
        movement.profitPercent,
        movement.cumProfit,
        movement.cumProfitPercent,
        trackingEnded ? status(fact ? getTrackingEndedMovementTime(fact) : "") : "",
      ];
    });
  });
  return { headers, rows };
};

export const RunPositions: React.FC<Props> = ({
  run,
  runResult,
  type,
  virtualizedTable = false,
}) => {
  // Derive execution-specific values from the actual run
  const isGroupRun = "_is_group_synthetic" in run && !!(run as any)._is_group_synthetic;
  const isLive = type === "execution" && !run.end_ts;
  // For group views, don't use run.id (it's the group ID, not an execution ID)
  const executionId = type === "execution" && !isGroupRun ? run.id : undefined;
  const symbolName = run.config?.symbol_name || "";
  const exchangeName = run.config?.exchange_name || "";
  const positions = sortRunPositions(run.positions as T.AppPosition[] | undefined);
  const onlyTrackingEnded = hasOnlyTrackingEndedPositions(positions);

  // Process positions into table data
  const tableRows: TableRow[] = [];
  let cumulativeProfit = 0;

  positions.forEach((position) => {
    const trackingEnded = isPositionTrackingEnded(position);
    if (position.movements && position.movements.length > 0) {
      // Exit is always SELL (2=SELL_SPOT, 4=SELL_FUTURE) regardless of direction.
      // For futures, both long and short open with BUY_FUTURE and close with SELL_FUTURE.
      // The direction (long/short) is tracked on the position, not the action.
      const exitActionIds = [2, 4];

      // Find the final exit movement for this position to avoid double-counting profit
      const closingExitMovementId = trackingEnded
        ? undefined
        : [...position.movements].filter((m) => exitActionIds.includes(m.action_id)).at(-1)?.id;

      position.movements.forEach((movement) => {
        const price = movement.price || 0;
        const amount = movement.amount || 0;
        const fees = movement.fees_usdt || 0;
        const positionSize = price * amount;
        const ts = trackingEnded
          ? getTrackingEndedMovementTime(movement)
          : movement.movement_ts || movement.timestamp || "";

        // Map action_id to movement type
        const getMovementType = (actionId: number) => {
          switch (actionId) {
            case 1: // BUY_SPOT
            case 3: // BUY_FUTURE
              return "BUY";
            case 2: // SELL_SPOT
            case 4: // SELL_FUTURE
              return "SELL";
            default:
              return `ACTION_${actionId}`;
          }
        };

        const movementType = getMovementType(movement.action_id);

        // Use backend-computed profit only for the closing exit movement
        let profit = 0;
        let profitPercent = 0;
        const isExitMovement = exitActionIds.includes(movement.action_id);
        const isPositionClosed = position.end_ts != null;
        const isFinalCloseMovement =
          isPositionClosed &&
          closingExitMovementId !== undefined &&
          isExitMovement &&
          movement.id === closingExitMovementId;

        if (!trackingEnded && isFinalCloseMovement) {
          // Final exit movement - use calculated profit (with fallback)
          const profitResult = calculatePositionProfit(position);
          profit = profitResult?.netProfit ?? 0;
          profitPercent = profitResult?.profitPct ?? 0;
          cumulativeProfit += profit;
        } else if (!trackingEnded && isExitMovement) {
          // TODO: Temporal - partial close profit calculation
          // The framework should support multi-order position-movements natively
          const partialParams: PartialCloseProfitParams = {
            exitPrice: price,
            exitAmount: amount,
            exitFees: fees,
            entryPrice: position.entry_price,
            entryAmount: position.entry_amount,
            collateralIn: position.collateral_in ?? null,
            directionName: position.direction_name,
            typeName: position.type_name,
          };
          profit = calculatePartialCloseProfit(partialParams);
          profitPercent = calculatePartialCloseProfitPercent(profit, partialParams);
          cumulativeProfit += profit;
        }
        const cumProfitPercent = safeCalculatePercentage(
          cumulativeProfit,
          position.entry_price,
          amount,
        );

        // Calculate duration from position start to this movement
        const duration = ts && position.start_ts ? calculateDuration(position.start_ts, ts) : "";

        tableRows.push({
          id: movement.id,
          position: position.id.toString(),
          type: movementType,
          dateTime: formatDateTimeUTC(ts),
          duration,
          price: `${price.toFixed(2)} USDT`,
          contracts: amount.toFixed(4),
          positionSize: positionSize.toFixed(2),
          leverage: position.leverage ? `${position.leverage}x` : "1x",
          collateral: trackingEnded
            ? (position.collateral_in?.toFixed(2) ?? "-")
            : (position.collateral_in || positionSize).toFixed(2),
          fees: trackingEnded ? (movement.fees_usdt?.toFixed(4) ?? "-") : fees.toFixed(4),
          profit: profit !== 0 ? profit.toFixed(4) : "",
          profitPercent: profitPercent !== 0 ? `${profitPercent.toFixed(2)}%` : "",
          cumProfit: cumulativeProfit !== 0 ? cumulativeProfit.toFixed(4) : "",
          cumProfitPercent: cumulativeProfit !== 0 ? `${cumProfitPercent.toFixed(2)}%` : "",
        });
      });
    }
    // No fallback - if a position has no movements, it means data is missing
    // Positions that are not tracking-ended stay out; tracking-ended rows receive a position-only display row.
  });

  // Get summary statistics from backend metrics - use ONLY backend-provided fields
  const metrics = run.metrics;
  const totalProfit = metrics?.cumulative_returns ?? 0;

  // Calculate ONLY fields NOT provided by backend (avg profit, fees, size, gross profit)
  // Backend provides avg_win_amount and avg_loss_amount separately, not avg of all positions
  const completedPositions = positions.filter(
    (p) => p.end_ts !== null && !isPositionTrackingEnded(p),
  );
  const totalGrossProfit = completedPositions.reduce(
    (sum, p) => sum + getPositionGrossProfit(p)!,
    0,
  );
  const totalFees = positions.reduce((sum, p) => sum + (p.total_fees_usdt || 0), 0);
  const totalFundingCost = positions.reduce((sum, p) => sum + (p.total_funding_fee_usdt ?? 0), 0);
  const totalSlippage = positions.reduce((sum, p) => sum + (p.total_slippage ?? 0), 0);
  const avgProfit = completedPositions.length > 0 ? totalProfit / completedPositions.length : 0;
  const avgFees = positions.length > 0 ? totalFees / positions.length : 0;
  const avgSize =
    positions.length > 0
      ? positions.reduce((sum, p) => sum + p.entry_price * p.entry_amount, 0) / positions.length
      : 0;

  // Calculate average duration for completed positions
  const avgDuration = (() => {
    if (completedPositions.length === 0) return "-";
    const totalDurationMs = completedPositions.reduce((sum, p) => {
      if (!p.start_ts || !p.end_ts) return sum;
      const start = new Date(p.start_ts).getTime();
      const end = new Date(p.end_ts).getTime();
      if (isNaN(start) || isNaN(end)) return sum;
      return sum + (end - start);
    }, 0);
    const avgMs = totalDurationMs / completedPositions.length;
    const hours = Math.floor(avgMs / (1000 * 60 * 60));
    const minutes = Math.floor((avgMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  })();

  // Convert table data to format expected by Hightable
  const convertToHightableData = () =>
    buildVirtualizedPositionRows(positions, tableRows, run, runResult);

  // Copy handler for all positions
  const copyAllPositionsToClipboard = () => {
    const runExport = buildRunExport(
      runResult,
      type,
      positions,
      tableRows,
      getAccountingCoverageMetrics(run),
    );
    const text = formatRunExportAsJSON(runExport);
    navigator.clipboard.writeText(text).catch((err) => {
      console.error("Failed to copy:", err);
    });
  };

  const renderVirtualizedTable = () => {
    const { headers, rows } = convertToHightableData();
    const dataFrame = arrayDataFrame([headers, ...rows]);

    return (
      <div style={{ height: "400px", width: "100%" }}>
        <HighTable data={dataFrame} styled={true} />
      </div>
    );
  };

  const renderRegularTable = () => {
    // Group movements by position
    // Create Open/Close pairs with full position objects
    // The first movement row supplies the entry column.
    // A financial close with multiple rows uses the last row for the close column.
    // Pairs by row order, not by BUY/SELL strings: futures shorts open with BUY_FUTURE (action 3 = "BUY"), not SELL.
    const positionsRows = buildPositionDisplayRows(positions, tableRows);

    const renderMovementCell = (
      row: PositionRow,
      type: "open" | "close",
      column: "left" | "right",
    ) => {
      const rowData = type === "open" ? row.open : row.close;
      if (!rowData) return null;

      const movement = row.position.movements?.find((m) => m.id === rowData.id);
      if (!movement) {
        console.log("Movement not found for rendering:", rowData.id);
        return null;
      }

      return (
        <RunMovementCell
          movement={movement}
          position={row.position}
          rowData={rowData}
          column={column}
          positionRow={row}
          isOpen={type === "open"}
        />
      );
    };

    const renderPositionRow = (row: PositionRow, index: number) => {
      return (
        <div
          key={row.positionOnly ? `position-${row.positionId}` : row.positionId}
          data-position-id={row.positionId}
          className={`PositionRow position-grid border-bottom ${index % 2 === 0 ? "bg-light-subtle" : ""}`}
        >
          {/* Open Column */}
          <div
            className={`PositionCell position-column open border-end ${row.open ? "position-data small" : ""}`}
          >
            {row.positionOnly ? (
              <PositionOnlyCell row={row} />
            ) : row.open ? (
              <>
                {renderMovementCell(row, "open", "left")}
                {renderMovementCell(row, "open", "right")}
              </>
            ) : (
              <div className="text-muted">No Open movement</div>
            )}
          </div>

          {/* Close Column */}
          <div className="PositionCell position-column close position-data small">
            {isPositionTrackingEnded(row.position) ? (
              <TrackingEndedFacts
                position={row.position}
                executionInterval={getPositionExecutionInterval(row.position, run, runResult)}
              />
            ) : row.close ? (
              <>
                {renderMovementCell(row, "close", "left")}
                {renderMovementCell(row, "close", "right")}
              </>
            ) : (
              <OpenPositionCell
                position={row.position}
                isLive={isLive}
                executionId={executionId ?? row.position.execution_id ?? undefined}
              />
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="runPositions" style={{ overflowX: "auto" }}>
        <div className="border rounded">
          {/* Header */}
          <div className="position-headers border-bottom fw-bold">
            <div className="p-2 border-end text-center">
              <div className="position-header open">Open</div>
            </div>
            <div className="p-2 position-relative d-flex align-items-center justify-content-center">
              <div className="position-header close">Close</div>
              <div className="position-absolute end-0 me-2 d-flex align-items-center gap-3">
                <CIcon
                  icon={cilArrowBottom}
                  size="sm"
                  className="text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const container = document.querySelector(".runPositions");
                    if (container) container.scrollIntoView({ behavior: "smooth", block: "end" });
                  }}
                  title="Scroll to last position"
                />
                <CIcon
                  icon={cilCopy}
                  size="sm"
                  className="text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={copyAllPositionsToClipboard}
                  title="Copy all positions as JSON"
                />
              </div>
            </div>
          </div>

          {/* Position rows - with execution separators for group views */}
          {positionsRows.length > 0 ? (
            (() => {
              const hasMultipleExecutions =
                new Set(positionsRows.map((r) => r.position.execution_id)).size > 1;
              return positionsRows.map((row, index) => {
                const currentExecId = row.position.execution_id;
                const prevExecId =
                  index > 0 ? positionsRows[index - 1].position.execution_id : null;
                const showExecHeader =
                  hasMultipleExecutions && currentExecId != null && currentExecId !== prevExecId;

                return (
                  <React.Fragment
                    key={row.positionOnly ? `position-${row.positionId}` : row.positionId}
                  >
                    {showExecHeader && (
                      <div className="execution-separator px-3 py-1 fw-semibold small border-bottom border-top text-center">
                        Execution #{currentExecId}
                      </div>
                    )}
                    {renderPositionRow(row, index)}
                  </React.Fragment>
                );
              });
            })()
          ) : (
            <div className="p-3 text-center text-muted border-bottom">No positions found</div>
          )}

          {/* Manual Order Row - only for live executions */}
          {isLive && executionId && (
            <ManualOrderRow
              executionId={executionId}
              exchangeName={exchangeName}
              symbolName={symbolName}
              rowIndex={positionsRows.length}
              run={run}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <CCard id="RunPositions" className="border-0">
      <CCardBody className="p-2">
        {/* Summary Statistics - Position-specific metrics only (duplicates removed) */}
        <AccountingCoverageNotice
          metrics={getAccountingCoverageMetrics(run)}
          positions={positions}
          quoteCurrency={runResult.symbol?.quote ?? null}
        />
        <div className="PositionsSummary mb-3">
          <div className="summaryColumn">
            <div className="SummaryItem pos-avgDuration">
              <strong>Avg Duration:</strong>
              <span>{avgDuration}</span>
            </div>
            <div className="SummaryItem pos-grossProfit">
              <strong>Gross Profit:</strong>
              {onlyTrackingEnded ? (
                <span className="PositionsSummaryUnknown text-muted">Unknown</span>
              ) : (
                <span>
                  <ColoredValue value={totalGrossProfit} threshold={0} type="usdt" />
                </span>
              )}
            </div>
            <div className="SummaryItem pos-netProfit">
              <strong>Net Profit:</strong>
              {onlyTrackingEnded ? (
                <span className="PositionsSummaryUnknown text-muted">Unknown</span>
              ) : (
                <span>
                  <ColoredValue value={totalProfit} threshold={0} type="usdt" />
                </span>
              )}
            </div>
          </div>
          <div className="summaryColumn">
            <div className="SummaryItem pos-avgProfit">
              <strong>Avg Profit:</strong>
              {onlyTrackingEnded ? (
                <span className="PositionsSummaryUnknown text-muted">Unknown</span>
              ) : (
                <span>
                  <ColoredValue value={avgProfit} threshold={0} type="usdt" />
                </span>
              )}
            </div>
            <div className="SummaryItem pos-avgSize">
              <strong>Avg Size:</strong>
              <span>
                <ColoredValue value={avgSize} type="usdt" />
              </span>
            </div>
            <div className="SummaryItem pos-totalSlippage">
              <strong>Slippage:</strong>
              <span>
                <ColoredValue
                  value={totalSlippage}
                  type="usdt"
                  decimals={4}
                  forceColor={totalSlippage !== 0 ? "loss" : undefined}
                />
              </span>
            </div>
          </div>
          <div className="summaryColumn">
            <div className="SummaryItem pos-avgFees">
              <strong>Avg Fees:</strong>
              <span>
                <ColoredValue value={avgFees} type="usdt" decimals={4} forceColor="loss" />
              </span>
            </div>
            <div className="SummaryItem pos-totalFees">
              <strong>Total Fees:</strong>
              <span>
                <ColoredValue value={totalFees} type="usdt" decimals={4} forceColor="loss" />
              </span>
            </div>
            <div className="SummaryItem pos-fundingCost">
              <strong>Funding Cost:</strong>
              <span>
                <ColoredValue value={totalFundingCost} type="usdt" decimals={4} threshold={0} />
              </span>
            </div>
          </div>
        </div>

        <hr />

        {/* Positions Table */}
        {virtualizedTable ? renderVirtualizedTable() : renderRegularTable()}
      </CCardBody>
    </CCard>
  );
};
